<script setup lang="ts">
import { ref, watch } from 'vue';

import { actionPointStatusTypeItems, type ActionPointForm, type ActionPointStatusTypeItem } from '@/types/actionPoint';
import type { Organization } from '@/types/organization';
import type { Risk } from '@/types/risk';
import type { VForm } from 'vuetify/components';

import { dateRange, required } from '@/utils/validation';

import CustomButton from '@/components/CustomButton.vue';
import Dialog from '@/components/dialogs/Dialog.vue';
import EntityChooseSelect from '@/components/dialogs/EntityChooseSelect.vue';
import DateField from '@/components/forms/DateField.vue';
import { Feature } from '@/enums/feature';
import { useActionPointStore } from '@/stores/actionPoint';
import { useAuthStore } from '@/stores/auth';
import { useModuleStore } from '@/stores/module';
import type { NameIdPair } from '@/types/nameIdPair';
import type { Priority } from '@/types/priority';
import type { Source } from '@/types/source';
import { maximumDate, minimumDate } from '@/utils/date';
import { processErrorWithFeedback } from '@/utils/error';
import { orgHasFeature } from '@/utils/organization';
import toast from '@/utils/toast';
import { storeToRefs } from 'pinia';
import { defineEmits, defineModel, defineProps } from 'vue';

const authStore = useAuthStore();
const actionPointStore = useActionPointStore();
const moduleStore = useModuleStore();

const props = defineProps<{
    modelValue: boolean;
    employees: NameIdPair[];
    teams: NameIdPair[];
    risks: Risk[];
    sources: Source[];
    priorities: Priority[];
    organization?: Organization | null;
}>();

const emit = defineEmits(['closeDialog', 'update:modelValue', 'add-list-item']);

const responsibleType = ref<'Employee' | 'Team'>();
const executorType = ref<'Employee' | 'Team'>();

const newActionPoint = ref<ActionPointForm>();

const isDialogOpen = defineModel<boolean>();

const form = ref<VForm>();
const isLoading = ref(false);

const availableActionPointStatusses = ref<ActionPointStatusTypeItem[]>();
const { isActionPointExecutorFeatureEnabled } = storeToRefs(authStore);

watch(
    () => isDialogOpen.value,
    async () => {
        if (isDialogOpen.value === false) form.value?.reset();
        newActionPoint.value = await getNewActionPointObject();
    }
);

async function getNewActionPointObject() {
    const actionPoint = { status: 'notStarted', module: moduleStore.modules.find((m) => m.name === 'RI&E')?._id } as ActionPointForm;

    const hasNoteRequiredWhenCompletingFeature = await orgHasFeature(Feature.NoteRequiredWhenCompletingActionPoint, props.organization?._id);
    availableActionPointStatusses.value = hasNoteRequiredWhenCompletingFeature
        ? actionPointStatusTypeItems.filter((s) => s.value !== 'done')
        : actionPointStatusTypeItems;

    if (moduleStore.modules.length == 1) actionPoint.module = moduleStore.modules[0]?._id;
    return actionPoint;
}

async function onConfirm() {
    try {
        const formValidation = await form.value?.validate();
        if (!formValidation?.valid) return;

        isLoading.value = true;

        if (!newActionPoint.value) {
            toast.error('ActionPointCreationFailed');
            return;
        }

        newActionPoint.value.organization = props.organization?._id ?? authStore.getCurrentOrganization()._id;

        const createdActionPoint = await actionPointStore.create(newActionPoint.value);
        const detailedActionPoint = await actionPointStore.getDetailsById(createdActionPoint._id);

        emit('add-list-item', detailedActionPoint);
        closeDialog();

        newActionPoint.value = await getNewActionPointObject();
    } catch (error) {
        processErrorWithFeedback('Actiepunt kon niet aangemaakt worden', error);
    } finally {
        isLoading.value = false;
    }
}

async function closeDialog() {
    form.value?.reset();
    newActionPoint.value = await getNewActionPointObject();
    emit('closeDialog');
}
</script>

<template>
    <Dialog v-model="isDialogOpen" title="Nieuw actiepunt" :isLoading="isLoading" persistent>
        <v-form ref="form" lazy-validation>
            <div class="v-col-12" v-if="newActionPoint">
                <v-text-field v-model="newActionPoint.action" label="Actie" required type="text" :rules="[required]" />
                <v-textarea v-model="newActionPoint.description" label="Beschrijving" type="text" />

                <v-select
                    v-if="moduleStore.areMultipleModulesAccessible"
                    v-model="newActionPoint.module"
                    label="Module"
                    required
                    :items="moduleStore.modules"
                    item-title="name"
                    item-value="_id"
                    :rules="[required]"
                />
                <DateField
                    v-model="newActionPoint.deadline"
                    label="Deadline"
                    :include-time="true"
                    :max="maximumDate"
                    :min="minimumDate"
                    :rules="[dateRange(minimumDate, maximumDate)]"
                />

                <EntityChooseSelect
                    v-model="newActionPoint.responsible"
                    v-model:type="responsibleType"
                    :employees="props.employees"
                    :teams="props.teams"
                    autocomplete-label="Verantwoordelijke"
                    entityTypeLabel="Wat wil je toewijzen als verantwoordelijke?"
                />

                <EntityChooseSelect
                    v-if="isActionPointExecutorFeatureEnabled"
                    v-model="newActionPoint.executor"
                    v-model:type="executorType"
                    :employees="props.employees"
                    :teams="props.teams"
                    autocomplete-label="Uitvoerder"
                    entityTypeLabel="Wat wil je toewijzen als uitvoerder?"
                />

                <v-select
                    v-model="newActionPoint.risk"
                    label="Risiconiveau"
                    required
                    :items="props.risks"
                    item-title="description"
                    item-value="_id"
                    :rules="[required]"
                />
                <v-select v-model="newActionPoint.priority" label="Prioriteit" :items="props.priorities" item-title="name" item-value="_id" />

                <v-autocomplete
                    v-model="newActionPoint.status"
                    label="Status"
                    :items="availableActionPointStatusses"
                    item-title="title"
                    item-value="value"
                    :rules="[required]"
                    required
                    clearable
                />

                <v-autocomplete v-model="newActionPoint.source" label="Bron" :items="props.sources" item-title="name" item-value="_id" clearable />
            </div>
        </v-form>
        <template #actions>
            <v-spacer />
            <CustomButton class="mr-3" @click="onConfirm" text="Aanmaken" />
            <CustomButton color="red" @click="closeDialog" text="Annuleren" />
        </template>
    </Dialog>
</template>
